export function nextQueueIndex(queueLength: number, queueIndex: number | null) {
  if (queueIndex === null || queueIndex + 1 >= queueLength) return null;
  return queueIndex + 1;
}

export function previousQueueIndex(queueIndex: number | null) {
  if (queueIndex === null || queueIndex <= 0) return null;
  return queueIndex - 1;
}

export type QueuePlaybackContext = {
  kind: 'catalog' | 'project' | 'playlist' | 'library' | 'generation' | 'delivery' | 'single';
  id: string;
  label: string;
};

type QueueIdentity = { id: string };

export type QueueModel<T extends QueueIdentity> = {
  canonical: readonly T[];
  context: QueuePlaybackContext | null;
  index: number | null;
  items: readonly T[];
  shuffle: boolean;
};

export function emptyQueue<T extends QueueIdentity>(shuffle = false): QueueModel<T> {
  return { canonical: [], context: null, index: null, items: [], shuffle };
}

function uniqueById<T extends QueueIdentity>(items: readonly T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function randomize<T>(items: readonly T[], random: () => number): T[] {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1));
    [result[index], result[target]] = [result[target]!, result[index]!];
  }
  return result;
}

export function startQueue<T extends QueueIdentity>(
  items: readonly T[],
  startIndex: number,
  context: QueuePlaybackContext,
  shuffle: boolean,
  random: () => number = Math.random,
): QueueModel<T> | null {
  const canonical = uniqueById(items);
  const requested = items[startIndex];
  if (!requested) return null;
  const canonicalIndex = canonical.findIndex((item) => item.id === requested.id);
  if (canonicalIndex < 0) return null;
  const queueItems = shuffle
    ? [
        canonical[canonicalIndex]!,
        ...randomize(
          canonical.filter((item) => item.id !== requested.id),
          random,
        ),
      ]
    : canonical;
  return {
    canonical,
    context,
    index: shuffle ? 0 : canonicalIndex,
    items: queueItems,
    shuffle,
  };
}

export function appendQueueItems<T extends QueueIdentity>(
  model: QueueModel<T>,
  contextId: string,
  items: readonly T[],
  random: () => number = Math.random,
): QueueModel<T> {
  if (model.context?.id !== contextId) return model;
  const known = new Set(model.canonical.map((item) => item.id));
  const additions = uniqueById(items).filter((item) => !known.has(item.id));
  if (!additions.length) return model;
  const canonical = [...model.canonical, ...additions];
  if (!model.shuffle || model.index === null) {
    return { ...model, canonical, items: [...model.items, ...additions] };
  }
  const traversed = model.items.slice(0, model.index + 1);
  const remaining = model.items.slice(model.index + 1);
  return {
    ...model,
    canonical,
    items: [...traversed, ...randomize([...remaining, ...additions], random)],
  };
}

export function setQueueShuffle<T extends QueueIdentity>(
  model: QueueModel<T>,
  enabled: boolean,
  random: () => number = Math.random,
): QueueModel<T> {
  if (model.shuffle === enabled) return model;
  if (model.index === null || !model.items[model.index]) return { ...model, shuffle: enabled };
  const traversed = model.items.slice(0, model.index + 1);
  const traversedIds = new Set(traversed.map((item) => item.id));
  const remaining = model.canonical.filter((item) => !traversedIds.has(item.id));
  return {
    ...model,
    items: [...traversed, ...(enabled ? randomize(remaining, random) : remaining)],
    shuffle: enabled,
  };
}

export type QueueRemoval<T extends QueueIdentity> =
  | { kind: 'close'; model: QueueModel<T> }
  | { kind: 'current'; item: T; model: QueueModel<T> }
  | { kind: 'unchanged'; model: QueueModel<T> }
  | { kind: 'updated'; model: QueueModel<T> };

export function removeQueueItems<T extends QueueIdentity>(
  model: QueueModel<T>,
  current: T | null,
  removedIds: ReadonlySet<string>,
): QueueRemoval<T> {
  if (!removedIds.size) return { kind: 'unchanged', model };
  const canonical = model.canonical.filter((item) => !removedIds.has(item.id));
  const items = model.items.filter((item) => !removedIds.has(item.id));
  if (canonical.length === model.canonical.length && items.length === model.items.length) {
    return { kind: 'unchanged', model };
  }

  if (!current || !removedIds.has(current.id)) {
    const index = current ? items.findIndex((item) => item.id === current.id) : -1;
    return {
      kind: 'updated',
      model: { ...model, canonical, items, index: index >= 0 ? index : null },
    };
  }

  const currentIndex = model.index ?? model.items.findIndex((item) => item.id === current.id);
  const successor = model.items
    .slice(Math.max(0, currentIndex + 1))
    .find((item) => !removedIds.has(item.id));
  if (!successor) {
    return { kind: 'close', model: emptyQueue<T>(model.shuffle) };
  }
  const index = items.findIndex((item) => item.id === successor.id);
  return {
    kind: 'current',
    item: successor,
    model: { ...model, canonical, items, index },
  };
}
